import styled from '@emotion/styled';
import React from 'react';
import {
	Css,
	DownloadIcon,
	Iframe,
	IframeWrap,
	Responsive,
	ResponsivePicture,
} from '../../../../ui';
import { IAsset } from '../../../../contentful';
import Dialog from '../../../content-sections/ui/components/Dialog';
import { Color } from '../../../../styles/Color';
import Button from '../../../../components/common/button/Button';
import { downloadMedia } from '../../../../utils/downloadMedia';
import { formatBytes } from '../domain/helpers/formatBytes';

interface IMediaItemDialogProps {
	item: IAsset;
	isDialogOpen: boolean;
	setDialogOpen: (isDialogOpen: boolean) => void;
	displayFilename?: boolean;
	allowDownload?: boolean;
}

export default function MediaItemDialog({
	item,
	isDialogOpen,
	setDialogOpen,
	displayFilename = true,
	allowDownload = true,
}: IMediaItemDialogProps) {
	const { title, description, file } = item.fields;
	const isVideo = item.type === 'video';
	const mediaType = isVideo ? 'video' : 'image';

	return (
		<Dialog
			isOpen={isDialogOpen}
			onClose={() => setDialogOpen(false)}
			mobileVariant="fullScreen"
		>
			<DialogContentWrap>
				{isVideo ? (
					<VideoWrapper>
						<Iframe
							url={file.url}
							title={title || description || file.fileName}
						/>
					</VideoWrapper>
				) : (
					<StyledResponsivePicture
						alt={title || description || file.fileName}
						source={file.url}
						rounded
						noMaxWidth
					/>
				)}
				{displayFilename && (
					<FileName isVideo={isVideo} data-translate>
						File name: {file.fileName}{' '}
						<span>Size: {formatBytes(file.details.size)}</span>
					</FileName>
				)}
			</DialogContentWrap>

			{allowDownload && (
				<StyledDownloadButton
					aria-label={`Download ${mediaType}`}
					isVideo={isVideo}
					icon={<DownloadIcon aria-hidden="true" />}
					onClick={() => {
						downloadMedia(file.fileName, file.url);
					}}
				>
					Download
				</StyledDownloadButton>
			)}
		</Dialog>
	);
}

const StyledResponsivePicture = styled(ResponsivePicture)`
	position: relative;

	img {
		margin-bottom: ${Css.pxToRem(-7)};
		max-height: 650px;
		object-fit: cover;

		@media (${Responsive.getMediaQueryForBreakpoint('desktopXL')}) {
			max-height: 800px;
		}

		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			margin-bottom: ${Css.pxToRem(-8.5)};
			border-radius: 0 !important;
		}
	}
`;

const FileName = styled.div<{ isVideo: boolean }>`
	position: absolute;
	background-color: ${Color.Neutral2};
	${({ isVideo }) =>
		isVideo ? `top: ${Css.pxToRem(24)};` : `bottom: ${Css.pxToRem(24)};`}
	left: ${Css.pxToRem(24)};

	padding: ${Css.pxToRem(8)};
	border-radius: 8px;

	span {
		margin-left: ${Css.pxToRem(16)};
	}

	@media only screen and (max-height: 576px) and (orientation: landscape) {
		bottom: ${Css.pxToRem(180)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		bottom: unset;
		top: unset;
		background-color: transparent;
		margin-top: ${Css.pxToRem(14)};
		padding: 0;
	}
`;

const DialogContentWrap = styled.div`
	width: 100%;
`;

const StyledDownloadButton = styled(Button)<{ isVideo: boolean }>`
	position: absolute;
	bottom: ${Css.pxToRem(24)};
	right: ${Css.pxToRem(24)};
	${({ isVideo }) => isVideo && `display: none;`}

	@media only screen and (max-height: 576px) and (orientation: landscape) {
		bottom: ${Css.pxToRem(180)};
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		position: fixed;
		${({ isVideo }) => isVideo && `display: flex;`}
	}
`;

const VideoWrapper = styled.div`
	${IframeWrap} {
		display: flex;
		flex-grow: 1;

		iframe {
			width: 100%;
			height: 100%;
		}
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		min-width: 100vw;
	}
`;
